import type { MotionState } from './motion';
import type { MovementTuning } from './tuning';

/**
 * The unified feedback bus (Team Asobi's architecture): ONE event shape that squash/stretch,
 * VFX, SFX, camera trauma, and (later) gamepad rumble all subscribe to. Events are DERIVED
 * purely from state transitions — the sim stays a pure function, and randomness lives only in
 * the subscribers. `surface` is the ground tag the M4 surface grid plumbs through the collision
 * service ('default' off the grid); LAND fires on the grounded tick, so `next` already holds
 * the freshly-landed-on surface.
 */
export type FeedbackType =
  | 'jump'
  | 'land'
  | 'skid'
  | 'teeter'
  | 'roll'
  | 'hoverStart'
  | 'hoverWarn'
  | 'hoverEnd'
  // Emitted by ENTITIES (never by deriveFeedback — the sim knows nothing of pots); type lives
  // here so every subscriber shares the one union. E0, ADR-0001.
  | 'break'
  // Exploration vertical (X0 seam 1) — emitted by exploration entities/orchestrator, never by
  // deriveFeedback; declared here so every subscriber shares the one union.
  | 'chargeGain'
  | 'stationOpen'
  | 'objectiveSet'
  | 'checkpointReached';

export interface FeedbackEvent {
  type: FeedbackType;
  /** 0..1 — impact/effort scale for gain, particle count, squash depth, rumble. */
  intensity: number;
  surface: string;
}

export function deriveFeedback(
  prev: MotionState,
  next: MotionState,
  t: MovementTuning,
): FeedbackEvent[] {
  const events: FeedbackEvent[] = [];
  const surface = next.groundSurface || 'default';

  if (next.msSinceTakeoff === 0 && prev.msSinceTakeoff !== 0) {
    events.push({ type: 'jump', intensity: 1, surface });
  }
  if (!prev.grounded && next.grounded) {
    const impact = Math.min(Math.abs(prev.vel.y) / 12, 1);
    events.push({ type: 'land', intensity: impact, surface });
  }
  if (!prev.skidding && next.skidding) {
    events.push({ type: 'skid', intensity: 1, surface });
  }
  if (!prev.teetering && next.teetering) {
    events.push({ type: 'teeter', intensity: 1, surface });
  }
  if (prev.rollMsLeft <= 0 && next.rollMsLeft > 0) {
    events.push({ type: 'roll', intensity: 1, surface });
  }
  if (prev.mode !== 'hover' && next.mode === 'hover') {
    events.push({ type: 'hoverStart', intensity: 1, surface });
  }
  if (
    next.mode === 'hover' &&
    prev.hoverFuelMs > t.hoverWarnMs &&
    next.hoverFuelMs <= t.hoverWarnMs
  ) {
    events.push({ type: 'hoverWarn', intensity: 1, surface });
  }
  if (prev.mode === 'hover' && next.mode !== 'hover') {
    events.push({ type: 'hoverEnd', intensity: next.grounded ? 0 : 1, surface });
  }

  return events;
}
